"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
export default function ComparisonHero() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const cards = [{
    eyebrow: "01 / STRATEGY",
    title: "OTA / REVENUE",
    body: "Maximize your property's visibility and conversion rates through data-driven OTA management and strategic listing optimization."
  }, {
    eyebrow: "02 / TECHNOLOGY",
    title: "TECH / STACK",
    body: "Expert recommendations and seamless implementation of hospitality tech stacks to streamline operations and boost direct bookings."
  }];
  return <section data-section-id="3961" id="comparisonhero" className="relative w-full h-screen min-h-[700px] overflow-hidden bg-slate-50 text-slate-900">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://hotelrevbaba.com/"
        }, {
          "@type": "ListItem",
          "position": 2,
          "name": "Comparisons",
          "item": "https://hotelrevbaba.com/comparisons"
        }, {
          "@type": "ListItem",
          "position": 3,
          "name": "RevNext vs HotelRevBaBa",
          "item": "https://hotelrevbaba.com/RevNextVsHotelRevBaBaComparisonPage"
        }]
      })
    }} />
      
      <div className="absolute inset-0 w-full h-full will-change-transform" style={{
      transform: `translate3d(0, ${scrollY * 0.15}px, 0)`
    }}>
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover grayscale" autoPlay muted loop playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_hero.png">
          <source src="/img/hotel_revenue_hero.mp4" type="video/mp4" />
        </video>
        
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_hero.png-wvc-sizes" alt="Modern hotel lobby interior with luxury aesthetic" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale -z-10" />
      </div>

      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(to bottom, hsla(222, 47%, 7%, 0) 0%, hsla(222, 47%, 7%, 0.8) 100%)"
    }} />
      
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(to right, hsla(222, 47%, 7%, 0.75) 0%, hsla(222, 47%, 7%, 0.25) 50%, hsla(222, 47%, 7%, 0) 100%)"
    }} />

      <div className="absolute top-0 left-0 right-0 z-20 flex items-start justify-between px-6 md:px-12 pt-8">
        <Link to="/" className="block">
          <WvcLogo className="h-7 w-auto" />
        </Link>
        <div className="hidden md:block font-sans text-[11px] tracking-widest uppercase text-white/70 text-right">
          REVENUE MANAGEMENT CONSULTING
          <br />
          SUNIL SINGH / EXPERT STRATEGIES
        </div>
      </div>

      <div className="relative z-10 h-full w-full px-6 md:px-12 lg:px-16 flex flex-col justify-center">
        <div className="max-w-[1100px]">
          <div className="font-sans text-[11px] tracking-[0.25em] uppercase text-amber-500 mb-6 md:mb-10">
            <span className="inline-block w-8 h-px align-middle bg-amber-500 mr-3" />
            Revenue Management Excellence
          </div>

          <h1 className="font-default font-bold text-white leading-[0.92] uppercase" style={{
          letterSpacing: "-0.04em",
          fontSize: "clamp(2.5rem, 8vw, 6rem)"
        }}>
            Comparison:
            <br />
            RevNext vs
            <br />
            HotelRevBaBa<span className="text-amber-500">.</span>
          </h1>

          <p className="font-sans text-white/85 mt-8 md:mt-10 max-w-xl" style={{
          fontSize: "clamp(1rem, 1.4vw, 1.25rem)"
        }}>
            Data-driven OTA strategies and expert revenue management to help hotel owners and investors maximize profitability.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="h-12 px-7 bg-amber-500 text-slate-900 hover:bg-amber-600 font-sans text-[11px] tracking-[0.2em] uppercase shadow-lg rounded-lg">
              <Link to="/ContactPage">Book a Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-7 bg-transparent border border-white/30 text-white hover:bg-white/10 hover:text-white font-sans text-[11px] tracking-[0.2em] uppercase rounded-lg">
              <Link to="/AboutPage">Our Philosophy</Link>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
            <Link to="/OurServicesPage" className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-amber-500 transition-colors">
              Explore Our Services
            </Link>
            <Link to="/CaseStudiesSuccessStoriesPage" className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-amber-500 transition-colors">
              Read Case Studies
            </Link>
            <Link to="/ContactPage" className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-amber-500 transition-colors">
              Contact for Demo
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 right-6 md:right-12 lg:right-16 bottom-24 md:bottom-20 hidden sm:block">
        <div className="relative w-[300px] md:w-[420px] lg:w-[460px] h-[300px] md:h-[340px]">
          <div data-index={0} className="absolute top-0 left-0 w-[260px] md:w-[340px] lg:w-[380px] backdrop-blur-sm border p-6 md:p-7 shadow-lg rounded-lg" style={{
          backgroundColor: "hsla(222, 47%, 7%, 0.6)",
          borderColor: "hsla(43, 46%, 55%, 0.3)"
        }}>
            <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-amber-500 mb-5">
              {cards[0].eyebrow}
            </div>
            <h3 className="font-default font-bold uppercase text-white leading-[0.95] mb-4" style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            letterSpacing: "-0.03em"
          }}>
              OTA REVENUE
              <br />
              MANAGEMENT
            </h3>
            <p className="font-sans text-[13px] md:text-sm leading-relaxed text-white/75">
              {cards[0].body}
            </p>
          </div>

          <div data-index={1} className="absolute bottom-0 right-0 w-[220px] md:w-[280px] lg:w-[300px] backdrop-blur-sm border p-5 md:p-6 shadow-lg rounded-lg" style={{
          backgroundColor: "hsla(222, 47%, 7%, 0.6)",
          borderColor: "hsla(43, 46%, 55%, 0.3)"
        }}>
            <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-amber-500 mb-4">
              {cards[1].eyebrow}
            </div>
            <h3 className="font-default font-bold uppercase text-white leading-[0.95] mb-3" style={{
            fontSize: "clamp(1.25rem, 1.9vw, 1.6rem)",
            letterSpacing: "-0.03em"
          }}>
              TECH
              <br />
              IMPLEMENTATION
            </h3>
            <p className="font-sans text-[12px] md:text-[13px] leading-relaxed text-white/75">
              {cards[1].body}
            </p>
          </div>
        </div>
      </div>

      <div className="sm:hidden absolute z-20 left-6 right-6 bottom-24 flex flex-col gap-3">
        {cards.map((c, i) => <div key={i} data-index={i} className="backdrop-blur-sm border p-5 shadow-lg rounded-lg" style={{
        backgroundColor: "hsla(222, 47%, 7%, 0.7)",
        borderColor: "hsla(43, 46%, 55%, 0.3)"
      }}>
            <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-amber-500 mb-3">
              {c.eyebrow}
            </div>
            <h3 className="font-default font-bold uppercase text-white leading-[0.95] mb-2" style={{
          fontSize: "1.3rem",
          letterSpacing: "-0.03em"
        }}>
              {c.title}
            </h3>
            <p className="font-sans text-[12px] leading-relaxed text-white/75">
              {c.body}
            </p>
          </div>)}
      </div>

      <div className="absolute left-6 md:left-12 bottom-10 z-20 hidden md:flex flex-col items-start gap-3">
        <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-white/70" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          EST. 2024 — REVENUE EXPERTISE
        </div>
      </div>

      <div className="absolute left-1/2 -translate-x-1/2 bottom-8 z-20 flex flex-col items-center gap-3">
        <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-white/60">
          Scroll
        </span>
        <div className="relative h-12 w-px overflow-hidden bg-white/15">
          <span className="absolute top-0 left-0 w-px bg-amber-500" style={{
          height: "100%",
          animation: "zenithScrollLine 2.2s ease-in-out infinite"
        }} />
        </div>
        <ArrowDown className="size-3 text-white/60" />
      </div>

      <style>{`
        @keyframes zenithScrollLine {
          0% { transform: translateY(-100%); }
          60% { transform: translateY(0%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>;
}